import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import MobileActions from "go-app/components/MobileActions/MobileActions";
import handleError from "go-app/services/errors";
import MessageEventModal from "go-component/components/MessageEvent/MessageEventModal";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import AttachmentsCard from "../../../../../../../../../../components/Attachment/AttachmentsCard";
import AppFormatResourceStatusLabel from "../../../../../../../../../../components/FormatStatus";
import { apiOrganization } from "../../../../../../../../../../services/Api/Organization/apiOrganization";
import { BundleApi, BundleStatus } from "../../../../../../../../../../services/Api/Organization/types";
import CopyDocumentModal from "../../../../components/CopyDocumentModal";
import BundlePreviewBasicInfoCard from "./BundlePreviewBasicInfoCard";
import BundlePreviewItemsCard from "./BundlePreviewItemsCard";

interface Props {
	bundle: BundleApi;
	customFieldsConfig: CustomFieldTemplateApi[];
	setBundle: (bundle: BundleApi) => void;
}

const BundlePreview = ({ bundle, customFieldsConfig, setBundle }: Props) => {
	const params: Record<string, any> = {
		include:
			"source_stock,destination_stock,custom_fields,items,items.product,items.items,items.items.product,tags,attachments",
	};
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const avatar = {
		color: "#FF9900",
		date: bundle.bundled_at,
	};
	const confirmation = useConfirmation();
	const isMobile = useWindowSize().isMobile;
	const { addSuccessFlash, addFlash } = useFlash();
	const history = useHistory();
	const [showLogs, setShowLogs] = useState(false);
	const [showCopyModal, setShowCopyModal] = useState(false);

	const renderSubtitle = () => (
		<ul>
			<li>{FormatDate(bundle.bundled_at)}</li>
			<li>{FormatMoney(bundle.total_mac_cost_net_money)}</li>
		</ul>
	);

	const renderTitle = () => (
		<>
			{t("modules.bundle.header.preview.title")} {bundle.document_number?.replace(" ", "-")}
			<AppFormatResourceStatusLabel
				status={bundle.status}
				customStatuses={[{ status: BundleStatus.CLOSED, label: "success" }]}
			/>
		</>
	);

	const handleChangeStatus = async (toStatus: "confirm" | "cancel" | "close_cancel" | "close" | "restore") => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			let res: BundleApi;
			switch (toStatus) {
				case "confirm":
					res = await apiOrganization.confirmBundle(bundle.id, params);
					break;
				case "cancel":
					res = await apiOrganization.cancelBundle(bundle.id, params);
					break;
				case "close_cancel":
					res = await apiOrganization.cancelCloseBundle(bundle.id, params);
					break;
				case "close":
					res = await apiOrganization.closeBundle(bundle.id, params);
					break;
				case "restore":
					res = await apiOrganization.restoreBundle(bundle.id, params);
					break;
			}
			setBundle(res);
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleRemoveBundle = async (toVoid?: boolean) => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: toVoid
					? t("confirmation.message.action", { ns: "lib" })
					: t("confirmation.message.remove", { ns: "lib" }),
			});
			await apiOrganization.removeBundle(bundle.id);
			addSuccessFlash(toVoid ? t("lib:common.flash.completed") : t("lib:common.flash.removed"));
			history.push(`/${organization.id}/documents/bundles`);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const buttons: ButtonProps[] = [
		{
			title: t("lib:common.action.edit"),
			variant: "primary",
			path: `/${organization.id}/documents/bundles/${bundle.id}/edit`,
		},
		{
			title: t("lib:common.action.confirm"),
			hide: bundle.status !== "DRAFT",
			action: () => handleChangeStatus("confirm"),
			variant: "light",
		},
		{
			title: t("lib:common.action.close"),
			hide: bundle.status !== "OPEN",
			action: () => handleChangeStatus("close"),
		},
		{
			title: t("common.action.cancel_close"),
			hide: bundle.status !== "CLOSED",
			action: () => handleChangeStatus("close_cancel"),
		},
		{
			title: t("lib:common.action.cancel"),
			hide: bundle.status !== "OPEN",
			action: () => handleChangeStatus("cancel"),
			dropdown: true,
		},
		{
			title: t("lib:common.action.restore"),
			hide: bundle.status !== "VOID",
			dropdown: true,
			action: () => handleChangeStatus("restore"),
		},
		{
			title: t("common.action.void"),
			hide: bundle.status === "VOID",
			dropdown: true,
			action: () => handleRemoveBundle(true),
		},
		{
			title: t("lib:common.action.remove"),
			dropdown: true,
			action: () => handleRemoveBundle(),
			hide: bundle.status === "OPEN" || bundle.status === "CLOSED",
		},
		{
			title: t("lib:common.word.logs"),
			action: () => setShowLogs(true),
			dropdown: true,
		},
		{
			title: t("lib:common.action.copy"),
			action: () => setShowCopyModal(true),
			dropdown: true,
		},
	];

	const mobileActions: MobileActionProps[] = [
		{
			title: t("lib:common.action.edit"),
			path: `/${organization.id}/documents/bundles/${bundle.id}/edit`,
		},
		{
			title: t("lib:common.action.confirm"),
			hide: bundle.status !== "DRAFT",
			action: () => handleChangeStatus("confirm"),
		},
		{
			title: t("lib:common.action.close"),
			hide: bundle.status !== "OPEN",
			action: () => handleChangeStatus("close"),
		},
		{
			title: t("common.action.cancel_close"),
			hide: bundle.status !== "CLOSED",
			action: () => handleChangeStatus("close_cancel"),
		},
		{
			title: t("lib:common.action.cancel"),
			hide: bundle.status !== "OPEN",
			action: () => handleChangeStatus("cancel"),
		},
		{
			title: t("lib:common.action.restore"),
			hide: bundle.status !== "VOID",
			action: () => handleChangeStatus("restore"),
		},
		{
			title: t("common.action.void"),
			hide: bundle.status === "VOID",
			action: () => handleRemoveBundle(true),
		},
		{
			title: t("lib:common.action.remove"),
			action: () => handleRemoveBundle(),
			hide: bundle.status === "OPEN" || bundle.status === "CLOSED",
		},
		{
			title: t("lib:common.word.logs"),
			action: () => setShowLogs(true),
		},
		{
			title: t("lib:common.action.copy"),
			action: () => setShowCopyModal(true),
		},
	];

	return (
		<div className="content-preview">
			<MobileActions actions={mobileActions} />
			<Header
				title={renderTitle()}
				buttons={isMobile ? [] : buttons}
				avatar={avatar}
				subtitle={renderSubtitle()}
				tags={bundle.tags?.map((tag) => tag.name)}
			/>
			<BundlePreviewBasicInfoCard bundle={bundle} customFieldsConfig={customFieldsConfig} />
			<BundlePreviewItemsCard bundle={bundle} />
			{bundle.file_attachments?.length > 0 && <AttachmentsCard attachments={bundle.file_attachments} />}
			{showLogs && (
				<MessageEventModal
					path={`/${organization.id}/logs/message_events`}
					resourceId={bundle.id}
					resourceType="BUNDLE"
					onHide={() => setShowLogs(false)}
					organizationId={organization.id}
				/>
			)}
			{showCopyModal && (
				<CopyDocumentModal
					show={showCopyModal}
					onHide={() => setShowCopyModal(false)}
					document={bundle}
					type="bundle"
				/>
			)}
		</div>
	);
};

export default BundlePreview;
